import React, { useEffect, useState } from "react";
import { Button, Dropdown, Menu, Modal, Popconfirm, Table, Row, Col, Space, Card, Typography, message, Form, Input, Select } from "antd";
import { MenuOutlined, PlusOutlined, DeleteOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { fetchData, postData, deleteData, showError } from "../../utils";
import AddProduct from "./AddProduct";
import { useBlockScreen, useRoot } from "../../rootContext";
import { Parser } from "json2csv";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [form] = Form.useForm();
  const [filterData, setFilterData] = useState({
    skus: '',
    platforms: [],
    brands: [],
    status: undefined, // undefined = all, true = active, false = inactive
  });
  const [selectOptions, setSelectOptions] = useState({
    brands: [],
    platforms: [],
  });

  const blocker = useBlockScreen();
  const root = useRoot();
  const userPermissions = root.user?.permissions || {};

  const loadProducts = async (page = 1, pageSize = 20) => {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        skus: filterData.skus || '',
        platforms: (filterData.platforms || []).join(','),
        brands: (filterData.brands || []).join(','),
        status: filterData.status !== undefined ? filterData.status.toString() : ''
      });

      const resp = await fetchData(
        `${window.SiteBasePath}/api/products/products?${queryParams}`,
        { blocker, root }
      );

      if (resp) {
        setProducts(resp.Data || []);
        setTotalCount(resp.TotalCount || 0);
      } else {
        setProducts([]);
        setTotalCount(0);
      }
    } catch (err) {
      showError("Failed to fetch products");
    }
  };

  // Load master data (brands, platforms) on mount
  useEffect(() => {
    fetchData(
      window.SiteBasePath + `/api/Products/master-data?types=platforms,brands`,
      { blocker, root }
    ).then((resp) => {
      if (resp) {
        setSelectOptions(resp);
      }
    });
  }, []);

  useEffect(() => {
    loadProducts();
  }, []);

  const handleDelete = async (id) => {
    try {
      await postData(`${window.SiteBasePath}/api/products/delete/${id}`, { blocker, root });
      loadProducts(pagination.current, pagination.pageSize);
    } catch (err) {
      showError("Failed to delete product");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) {
      message.warning("Please select at least one product to delete.");
      return;
    }

    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} products?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const resp = await postData(`${window.SiteBasePath}/api/products/bulk-delete`, selectedRowKeys, { blocker, root });
          message.success(resp?.Message || "Selected products deleted successfully.");
          setSelectedRowKeys([]);
          loadProducts(pagination.current, pagination.pageSize);
        } catch (err) {
          showError("Failed to delete selected products");
        }
      },
    });
  };


  const bulkUpdateStatus = () => {
    if (bulkStatus === null) {
      message.warning("Please select a status first.");
      return;
    }
    if (!selectedRowKeys.length) {
      message.warning("Please select at least one product.");
      return;
    }

    Modal.confirm({
      title: "Confirm Status Update",
      content: `Are you sure you want to set ${selectedRowKeys.length} product(s) to ${bulkStatus ? "Active" : "Inactive"
        }?`,
      okText: "Yes, Update",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await postData("/api/products/bulk-update-status", {
            productIds: selectedRowKeys,
            status: bulkStatus,
          });

          message.success("Status updated successfully");
          loadProducts(pagination.current, pagination.pageSize); // refresh table
          setBulkStatus(null);
          setSelectedRowKeys([]); // clear selection
        } catch (err) {
          showError(err);
        }
      },
    });
  };


  const columns = [
    { title: "Sku", dataIndex: "Sku", key: "Sku" },
    { title: "Brand", dataIndex: "Brand", key: "Brand" },
    { title: "Category", dataIndex: "Category", key: "Category" },
    { title: "Title", dataIndex: "Title", key: "Title" },
    {
      title: "Status",
      dataIndex: "Active",
      key: "Active",
      render: (active) => (
        <span style={{ color: active ? '#52c41a' : '#ff4d4f' }}>
          {active ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      title: "Platforms",
      dataIndex: "Platforms",
      key: "Platforms",
      render: (platforms) =>
        platforms && platforms.length ? platforms.join(", ") : "-",
    },
    {
      title: "Suppliers",
      dataIndex: "Suppliers",
      key: "Suppliers",
      render: (suppliers) =>
        suppliers && suppliers.length
          ? suppliers.join(", ")
          : "-",
    },

    {
      title: "Supplier SKUs",
      dataIndex: "SupplierSkus",
      key: "SupplierSkus",
      render: (_, record) => {
        const skus = record?.SupplierSkus;
        if (!skus || Object.keys(skus).length === 0) return "-";

        return Object.entries(skus).map(([supplier, sku]) => (
          <div key={supplier}>
            <strong>{supplier}:</strong> {sku}
          </div>
        ));
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => {
        const menuItems = [];

        if (userPermissions.updateProduct) {
          menuItems.push(
            <Menu.Item key="edit" onClick={() => {
              setEditingProductId(record.Id);
              setModalVisible(true);
            }}>
              Edit
            </Menu.Item>
          );
        }

        if (userPermissions.deleteProduct) {
          menuItems.push(
            <Menu.Item key="delete">
              <Popconfirm
                title="Are you sure you want to delete this product?"
                onConfirm={() => handleDelete(record.Id)}
              >
                Delete
              </Popconfirm>
            </Menu.Item>
          );
        }

        // Only render the dropdown if there are actions available
        if (menuItems.length === 0) {
          return null;
        }

        return (
          <Dropdown overlay={<Menu>{menuItems}</Menu>} trigger={["click"]}>
            <Button icon={<MenuOutlined />} />
          </Dropdown>
        );
      },
    },
  ];


  const exportToCsv = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: "1",
        pageSize: "100000", // fetch all
        skus: filterData.skus || "",
        platforms: (filterData.platforms || []).join(","),
        brands: (filterData.brands || []).join(","),
        status: filterData.status !== undefined ? filterData.status.toString() : "",
      });

      const resp = await fetchData(
        `${window.SiteBasePath}/api/products/products?${queryParams}`,
        { blocker, root }
      );

      if (!resp || !resp.Data || resp.Data.length === 0) {
        message.info("No products found to export.");
        return;
      }

      // 🔹 Flatten your product data
      const rows = resp.Data.map((p) => ({
        Sku: p.Sku,
        Brand: p.Brand,
        Category: p.Category,
        Title: p.Title,
        Status: p.Active ? "Active" : "Inactive",
        Platforms: p.Platforms?.join(", ") || "-",
        Suppliers: p.Suppliers?.join(", ") || "-",
        SupplierSkus: p.SupplierSkus
          ? Object.entries(p.SupplierSkus)
            .map(([supplier, sku]) => `${supplier}: ${sku}`)
            .join(" | ")
          : "-",
      }));

      // 🔹 Convert JSON → CSV
      const parser = new Parser();
      const csv = parser.parse(rows);

      // 🔹 Create a Blob manually
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      // 🔹 Create a hidden link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `Products_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (err) {
      showError("Failed to export CSV");
      console.error(err);
    }
  };

  return (
    <Card style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>Products</Typography.Title>
          <Typography.Text type="secondary">Manage products — add, edit or delete</Typography.Text>
        </Col>
        <Col>
          <Space>
            {userPermissions.plateformSettings && (
              //button for platform management
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  window.location.href = '/platforms';
                }}
              >
                Manage Platforms
              </Button>
            )}
            {userPermissions.deleteProduct && (
              <Button
                icon={<DeleteOutlined />}
                danger
                disabled={!selectedRowKeys.length}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            )}
            {userPermissions.updateProduct && (
              <>
                <Select
                  placeholder="Set status..."
                  style={{ width: 150 }}
                  value={bulkStatus}
                  onChange={(val) => setBulkStatus(val)}
                  options={[
                    { value: true, label: "Active" },
                    { value: false, label: "Inactive" },
                  ]}
                />
                <Button
                  type="primary"
                  disabled={!selectedRowKeys.length || bulkStatus === null}
                  onClick={bulkUpdateStatus}
                >
                  Update Status
                </Button>
              </>
            )}

            {userPermissions.addProduct && (
              <Button
                type="primary"
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  setEditingProductId(null);
                  setModalVisible(true);
                }}
              >
                Add Product
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: "10px" }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="SKUs">
                <Input
                  value={filterData.skus}
                  placeholder="0000, 1111, ..."
                  onChange={(e) => {
                    setFilterData(prev => ({
                      ...prev,
                      skus: e.target.value
                    }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Platforms">
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  value={filterData.platforms}
                  onChange={(value) => {
                    setFilterData(prev => ({
                      ...prev,
                      platforms: value
                    }));
                  }}
                  options={(selectOptions.platforms || []).map((x) => ({
                    value: x.Id,
                    label: x.Name
                  }))}
                  placeholder="Select Platforms..."
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item label="Brands">
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  value={filterData.brands}
                  onChange={(value) => {
                    setFilterData(prev => ({
                      ...prev,
                      brands: value
                    }));
                  }}
                  options={(selectOptions.brands || []).map((x) => ({
                    value: x,
                    label: x
                  }))}
                  placeholder="Select Brands..."
                  maxTagCount="responsive"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Status">
                <Select
                  value={filterData.status}
                  onChange={(value) => {
                    setFilterData(prev => ({
                      ...prev,
                      status: value
                    }));
                  }}
                  options={[
                    { value: undefined, label: 'All' },
                    { value: true, label: 'Active' },
                    { value: false, label: 'Inactive' }
                  ]}
                  placeholder="Select Status"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between" align="middle">
            <Col>
              <Button onClick={exportToCsv} type="default">
                Export to Excel
              </Button>
            </Col>
            <Col>
              <Space>
                <Button
                  onClick={() => {
                    setFilterData({
                      skus: '',
                      platforms: [],
                      brands: [],
                      status: undefined
                    });
                    loadProducts();
                  }}
                >
                  Reset
                </Button>
                <Button type="primary" onClick={() => loadProducts()}>
                  Search
                </Button>
              </Space>
            </Col>
          </Row>

        </Form>
      </Card>

      <Table
        dataSource={products}
        columns={columns}
        rowKey="Id"
        pagination={{
          total: totalCount,
          pageSize: pagination.pageSize,
          current: pagination.current,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            loadProducts(page, pageSize);
          },
        }}
        scroll={{ x: true }} // ✅ allow horizontal scroll if columns overflow
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        size="middle"
        bordered
      />

      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
        destroyOnClose
        maskClosable={false}
      >
        <AddProduct
          product={products.find(p => p.Id === editingProductId) || null}
          onClose={() => setModalVisible(false)}
          onSuccess={() => {
            loadProducts();
          }}
        />
      </Modal>
    </Card>
  );
};

export default Products;
